import { MagicWand, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { FONT_FAMILIES, HEADER_COLORS, FONT_WEIGHTS } from "@/lib/typographyConstants";
import TypographyPanel from "./TypographyPanel";

// Re-exports kept so existing consumers of LayoutControls' typography
// constants (imports elsewhere) continue to work while the constants
// themselves now live in the shared module (breaks the circular import
// with TypographyPanel).
export { FONT_FAMILIES, HEADER_COLORS, FONT_WEIGHTS };

const PAGE_OPTIONS = [
  { id: "a4", label: "A4" },
  { id: "letter", label: "US Letter" },
];
const DENSITY_OPTIONS = [
  { id: "comfortable", label: "Comfortable" },
  { id: "compact", label: "Compact" },
];
// Extended range: 8-14 pt. Rendered as a dropdown to keep the panel compact.
const FONT_SIZE_OPTIONS = [
  { id: 0.8, label: "8 pt" },
  { id: 0.9, label: "9 pt" },
  { id: 1.0, label: "10 pt" },
  { id: 1.1, label: "11 pt" },
  { id: 1.2, label: "12 pt" },
  { id: 1.3, label: "13 pt" },
  { id: 1.4, label: "14 pt" },
];

export default function LayoutControls({
  pageSize,
  setPageSize,
  density,
  setDensity,
  fontScale,
  setFontScale,
  fontFamily,
  setFontFamily,
  descriptionFontFamily,
  setDescriptionFontFamily,
  lineSpacing,
  setLineSpacing,
  h1FontWeight,
  setH1FontWeight,
  h2FontWeight,
  setH2FontWeight,
  bodyFontWeight,
  setBodyFontWeight,
  h1Color,
  setH1Color,
  h2Color,
  setH2Color,
  jobTitleFontSize,
  setJobTitleFontSize,
  jobTitleFontWeight,
  setJobTitleFontWeight,
  jobTitleColor,
  setJobTitleColor,
  jobTitleFontFamily,
  setJobTitleFontFamily,
  companyFontSize,
  setCompanyFontSize,
  companyFontWeight,
  setCompanyFontWeight,
  companyColor,
  setCompanyColor,
  companyFontFamily,
  setCompanyFontFamily,
  resumeNameFontSize,
  setResumeNameFontSize,
  resumeNameFontFamily,
  setResumeNameFontFamily,
  sectionHeaderFontSize,
  setSectionHeaderFontSize,
  sectionHeaderFontFamily,
  setSectionHeaderFontFamily,
  descriptionFontSize,
  setDescriptionFontSize,
  descriptionFontWeight,
  setDescriptionFontWeight,
  descriptionColor,
  setDescriptionColor,
  contactInfoFontSize,
  setContactInfoFontSize,
  contactInfoFontWeight,
  setContactInfoFontWeight,
  contactInfoColor,
  setContactInfoColor,
  contactInfoFontFamily,
  setContactInfoFontFamily,
  workDescriptionFontSize,
  setWorkDescriptionFontSize,
  workDescriptionFontWeight,
  setWorkDescriptionFontWeight,
  workDescriptionColor,
  setWorkDescriptionColor,
  workDescriptionFontFamily,
  setWorkDescriptionFontFamily,
  dateFontSize,
  setDateFontSize,
  dateFontWeight,
  setDateFontWeight,
  dateColor,
  setDateColor,
  dateFontFamily,
  setDateFontFamily,
  locationFontSize,
  setLocationFontSize,
  locationFontWeight,
  setLocationFontWeight,
  locationColor,
  setLocationColor,
  locationFontFamily,
  setLocationFontFamily,
  bodyTypography,
  updateBodyCategory,
  autoFitting,
  onAutoFit,
  onResetAll,
}) {
  return (
    <>
      {/* Reset-all button — sits above every other control so it's the first
          thing users see in the Editor panel. Destructive action, so uses a
          crimson accent + confirmation dialog handled by the parent. */}
      {onResetAll && (
        <div className="mt-5" data-testid="reset-all-row">
          <button
            type="button"
            onClick={onResetAll}
            className="w-full flex items-center justify-center gap-2 py-2.5 border border-[#FF2A00]/40 bg-white text-[#FF2A00] text-xs font-mono uppercase tracking-widest hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] transition-colors"
            data-testid="reset-all-button"
            title="Restore all typography settings and reset the résumé content back to the built-in defaults"
          >
            <ArrowsCounterClockwise size={13} weight="bold" />
            Reset everything to defaults
          </button>
          <div className="mt-1.5 text-[10px] text-neutral-500 leading-snug">
            Restores every typography knob (fonts, sizes, weights, colours, line spacing, Work-Experience overrides) AND replaces the résumé content with the built-in sample.
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-4" data-testid="layout-controls">
        <div>
          <div className="label-mono mb-1.5">Page size</div>
          <div className="flex border border-black/10">
            {PAGE_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => setPageSize(o.id)}
                className={`flex-1 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${pageSize === o.id ? "bg-neutral-950 text-white" : "hover:bg-neutral-100"}`}
                data-testid={`page-size-${o.id}`}
              >{o.label}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="label-mono mb-1.5">Density</div>
          <div className="flex border border-black/10">
            {DENSITY_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => setDensity(o.id)}
                className={`flex-1 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${density === o.id ? "bg-neutral-950 text-white" : "hover:bg-neutral-100"}`}
                data-testid={`density-${o.id}`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Header font + Description font + Font Size selectors removed in
          iter 43/44 — font family AND size are now controlled per-category
          inside the Typography panel below (each category has its own
          size + family selectors). Line Spacing stays as a global multiplier
          for non-tagged text. */}

      <div className="mt-4" data-testid="line-spacing-row">
        <div className="flex items-center justify-between mb-1.5">
          <div className="label-mono">Line spacing</div>
          <div className="text-[10px] font-mono text-neutral-500" data-testid="line-spacing-value">
            {Number(lineSpacing).toFixed(2)}×
          </div>
        </div>
        <input
          type="range"
          min="0.8"
          max="2"
          step="0.05"
          value={lineSpacing}
          onChange={(e) => setLineSpacing(parseFloat(e.target.value))}
          className="w-full h-1.5 accent-neutral-950 cursor-pointer"
          data-testid="line-spacing-slider"
          aria-label="Line spacing multiplier"
        />
        <div className="mt-1 flex justify-between text-[9px] font-mono text-neutral-400 uppercase tracking-widest">
          <span>Tight · 0.8</span>
          <span>Default · 1.0</span>
          <span>Loose · 2.0</span>
        </div>
      </div>

      <TypographyPanel
        fontFamily={fontFamily}
        descriptionFontFamily={descriptionFontFamily}
        h1FontWeight={h1FontWeight}
        setH1FontWeight={setH1FontWeight}
        h2FontWeight={h2FontWeight}
        setH2FontWeight={setH2FontWeight}
        h1Color={h1Color}
        setH1Color={setH1Color}
        h2Color={h2Color}
        setH2Color={setH2Color}
        setDescriptionFontFamily={setDescriptionFontFamily}
        descriptionFontSize={descriptionFontSize}
        setDescriptionFontSize={setDescriptionFontSize}
        descriptionFontWeight={descriptionFontWeight}
        setDescriptionFontWeight={setDescriptionFontWeight}
        descriptionColor={descriptionColor}
        setDescriptionColor={setDescriptionColor}
        contactInfoFontSize={contactInfoFontSize}
        setContactInfoFontSize={setContactInfoFontSize}
        contactInfoFontWeight={contactInfoFontWeight}
        setContactInfoFontWeight={setContactInfoFontWeight}
        contactInfoColor={contactInfoColor}
        setContactInfoColor={setContactInfoColor}
        contactInfoFontFamily={contactInfoFontFamily}
        setContactInfoFontFamily={setContactInfoFontFamily}
        workDescriptionFontSize={workDescriptionFontSize}
        setWorkDescriptionFontSize={setWorkDescriptionFontSize}
        workDescriptionFontWeight={workDescriptionFontWeight}
        setWorkDescriptionFontWeight={setWorkDescriptionFontWeight}
        workDescriptionColor={workDescriptionColor}
        setWorkDescriptionColor={setWorkDescriptionColor}
        workDescriptionFontFamily={workDescriptionFontFamily}
        setWorkDescriptionFontFamily={setWorkDescriptionFontFamily}
        resumeNameFontSize={resumeNameFontSize}
        setResumeNameFontSize={setResumeNameFontSize}
        resumeNameFontFamily={resumeNameFontFamily}
        setResumeNameFontFamily={setResumeNameFontFamily}
        sectionHeaderFontSize={sectionHeaderFontSize}
        setSectionHeaderFontSize={setSectionHeaderFontSize}
        sectionHeaderFontFamily={sectionHeaderFontFamily}
        setSectionHeaderFontFamily={setSectionHeaderFontFamily}
        jobTitleFontSize={jobTitleFontSize}
        setJobTitleFontSize={setJobTitleFontSize}
        jobTitleFontWeight={jobTitleFontWeight}
        setJobTitleFontWeight={setJobTitleFontWeight}
        jobTitleColor={jobTitleColor}
        setJobTitleColor={setJobTitleColor}
        jobTitleFontFamily={jobTitleFontFamily}
        setJobTitleFontFamily={setJobTitleFontFamily}
        companyFontSize={companyFontSize}
        setCompanyFontSize={setCompanyFontSize}
        companyFontWeight={companyFontWeight}
        setCompanyFontWeight={setCompanyFontWeight}
        companyColor={companyColor}
        setCompanyColor={setCompanyColor}
        companyFontFamily={companyFontFamily}
        setCompanyFontFamily={setCompanyFontFamily}
        dateFontSize={dateFontSize}
        setDateFontSize={setDateFontSize}
        dateFontWeight={dateFontWeight}
        setDateFontWeight={setDateFontWeight}
        dateColor={dateColor}
        setDateColor={setDateColor}
        dateFontFamily={dateFontFamily}
        setDateFontFamily={setDateFontFamily}
        locationFontSize={locationFontSize}
        setLocationFontSize={setLocationFontSize}
        locationFontWeight={locationFontWeight}
        setLocationFontWeight={setLocationFontWeight}
        locationColor={locationColor}
        setLocationColor={setLocationColor}
        locationFontFamily={locationFontFamily}
        setLocationFontFamily={setLocationFontFamily}
        bodyTypography={bodyTypography}
        updateBodyCategory={updateBodyCategory}
      />

      <div className="mt-4" data-testid="auto-fit-row">
        <button
          onClick={onAutoFit}
          disabled={autoFitting}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-neutral-950 bg-white text-neutral-950 text-xs font-mono uppercase tracking-widest hover:bg-neutral-950 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="auto-fit-button"
          title="Tries compact density + smaller font to squeeze the resume onto a single page. If it can't, any overflow stays on page 2."
        >
          <MagicWand size={13} weight="fill" />
          {autoFitting ? "Fitting…" : "Auto-fit to one page"}
        </button>
        <div className="mt-1.5 text-[10px] text-neutral-500 leading-snug">
          Tightens density + font size to fit on 1 page. Overflow content stays on page 2 if it truly doesn&apos;t fit.
        </div>
      </div>
    </>
  );
}
